import { useState } from "react";
import { Mail, Phone, ChevronDown } from "lucide-react";
import { PHONE_COUNTRY_CODES, type PhoneCountryCode } from "@/data/phoneCountryCodes";

type ContactMode = "email" | "phone";

interface EmailOrPhoneInputProps {
  mode: ContactMode;
  onModeChange: (mode: ContactMode) => void;
  emailValue: string;
  onEmailChange: (value: string) => void;
  phoneValue: string;
  onPhoneChange: (value: string) => void;
  phoneCountry: PhoneCountryCode;
  onPhoneCountryChange: (country: PhoneCountryCode) => void;
  required?: boolean;
}

const EmailOrPhoneInput = ({
  mode,
  onModeChange,
  emailValue,
  onEmailChange,
  phoneValue,
  onPhoneChange,
  phoneCountry,
  onPhoneCountryChange,
  required = true,
}: EmailOrPhoneInputProps) => {
  const [showCountryList, setShowCountryList] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm font-bold text-foreground">
          {mode === "email" ? "E-mail" : "Téléphone"}
        </label>
        <button
          type="button"
          onClick={() => onModeChange(mode === "email" ? "phone" : "email")}
          className="text-xs font-bold text-primary hover:underline flex items-center gap-1"
        >
          {mode === "email" ? (
            <><Phone className="w-3 h-3" /> Utiliser le téléphone</>
          ) : (
            <><Mail className="w-3 h-3" /> Utiliser l'e-mail</>
          )}
        </button>
      </div>

      {mode === "email" ? (
        <div className="flex items-center gap-3 bg-muted rounded-2xl px-4 py-3">
          <Mail className="w-4 h-4 text-muted-foreground" />
          <input
            type="email"
            value={emailValue}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="mon@email.com"
            className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none"
            required={required}
          />
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-center gap-0 bg-muted rounded-2xl overflow-hidden">
            {/* Country code selector */}
            <button
              type="button"
              onClick={() => setShowCountryList(!showCountryList)}
              className="flex items-center gap-1 px-3 py-3 border-r border-border/50 hover:bg-muted/80 transition-colors shrink-0"
            >
              <span className="text-base">{phoneCountry.flag}</span>
              <span className="text-xs font-bold text-foreground">{phoneCountry.dialCode}</span>
              <ChevronDown className="w-3 h-3 text-muted-foreground" />
            </button>
            {/* Phone input */}
            <input
              type="tel"
              value={phoneValue}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                onPhoneChange(val);
              }}
              placeholder="XX XX XX XX XX"
              className="flex-1 bg-transparent text-sm font-medium text-foreground placeholder:text-muted-foreground outline-none px-3 py-3"
              required={required}
            />
          </div>

          {/* Country dropdown */}
          {showCountryList && (
            <div className="absolute z-50 top-full mt-1 left-0 right-0 bg-card border border-border rounded-2xl shadow-lg max-h-48 overflow-y-auto">
              {PHONE_COUNTRY_CODES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    onPhoneCountryChange(c);
                    setShowCountryList(false);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors text-sm ${
                    phoneCountry.code === c.code ? "bg-primary/5 text-primary font-bold" : "text-foreground"
                  }`}
                >
                  <span>{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-xs text-muted-foreground font-mono">{c.dialCode}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailOrPhoneInput;
export type { ContactMode };
